import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { Subscription } from "rxjs";

import { abrirDialogoNuevaVersion } from "../../modal-agregar-version/modal-agregar-version.component";
import { descargarEvidencia } from "../descarga-evidencia";
import {
  CODIGOS_VERSIONES,
  DetalleVersion,
  EjecucionPrueba,
  EvidenciaPrueba,
  IncidenciaVersion,
  VersionResumen,
} from "../etapas-versiones.model";
import { EtapasVersionesService } from "../etapas-versiones.service";
import { ChipEstado, SIN_DATO, chipEstadoVersion, fechaCorta, fechasDeEtapa, textoCerradaDesde } from "../etapas-versiones.util";
import { claseEstatus, clasePrioridad, nombrePrioridad } from "../incidencias-prueba.util";
import { rutaListaProyectos, rutaPaginaProyecto } from "../proyecto-pagina.rutas";
import {
  chipResultado,
  lineaCasos,
  nombreCiclo,
  nombreDeEvidencia,
  porcentajeAprobados,
  primeras,
  textoCasosEjecutados,
  textoCreadaPor,
  textoDeCasos,
  textoMostrando,
} from "../pruebas-version.util";
import {
  ResultadoRegistrarEjecucion,
  abrirRegistrarEjecucion,
} from "../registrar-ejecucion/registrar-ejecucion.component";

/** Una ejecución de la tabla, ya con sus textos. */
interface FilaEjecucion {
  ejecucion: EjecucionPrueba;
  fecha: string;
  titulo: string;
  casos: string;
  lineaCasos: string;
  porcentaje: number | null;
  chip: ChipEstado;
  evidencias: { evidencia: EvidenciaPrueba; nombre: string }[];
}

/** Una incidencia de las dos tablas, ya con sus textos. */
interface FilaIncidencia {
  incidencia: IncidenciaVersion;
  folio: string;
  prioridad: string;
  clasePrioridad: string;
  claseEstatus: string;
  detectadaEn: string;
  fechaResolucion: string;
}

/** La ruta del detalle QA de una incidencia: abre con la liga sola (detallesincidencia carga por el :id). */
const DETALLE_QA = "/INCIDENCIAS/INC_QA/detalle";

/**
 * Gestión de Proyectos · detalle de versión (A2; plan
 * docs/superpowers/plans/2026-10-13-etapas-y-versiones.md §8, maqueta
 * A2-Detalle-version.dc.html): VISUALIZARPROY/proyecto/:id/version/:idVersion
 * en los cuatro prefijos. Lo lee de PROYECTOS_VERSION_DETALLE y no recalcula:
 *
 * - Encabezado con el estado (Vigente o Cerrada), el ambiente y quién la
 *   creó; «Editar versión» (T1 en edición) y «Registrar pruebas» (T2) solo
 *   con permiso (D4 y D8).
 * - «Datos de la versión» y los indicadores; los casos son los de la última
 *   ejecución, con «Última de N» (D20).
 * - Ejecuciones con quien ejecutó y su rol, la evidencia (se baja con el
 *   token) y «Editar ejecución» (T2 en edición, D5).
 * - Detectadas en la versión y resueltas en ella: 5 y «Ver las N». El folio
 *   abre el detalle QA de la incidencia.
 */
@Component({
  selector: "app-detalle-version",
  standalone: true,
  templateUrl: "./detalle-version.component.html",
  styleUrls: ["./detalle-version.component.css"],
  imports: [CommonModule, RouterLink],
})
export class DetalleVersionComponent implements OnInit, OnDestroy {
  readonly sinDato = SIN_DATO;
  readonly detalleQA = DETALLE_QA;

  idProyecto = "";
  idVersion = "";
  detalle: DetalleVersion | null = null;
  ejecuciones: FilaEjecucion[] = [];
  detectadas: FilaIncidencia[] = [];
  resueltas: FilaIncidencia[] = [];
  todasDetectadas = false;
  todasResueltas = false;
  rutaLista = "";
  rutaProyecto = "";
  /** ?pestana=etapas&etapa=<id> de la página del proyecto (las migas); se arma al leer la versión. */
  queryEtapa: Record<string, string> = { pestana: "etapas" };
  cargando = false;
  error = "";

  private readonly subs: Subscription[] = [];
  private lectura?: Subscription;
  private destruido = false;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly servicio: EtapasVersionesService,
    private readonly dialog: MatDialog,
    private readonly snackBar: MatSnackBar,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.rutaLista = rutaListaProyectos(this.router.url);
    this.subs.push(
      this.route.paramMap.subscribe((parametros) => {
        const idProyecto = (parametros.get("id") ?? "").trim();
        const idVersion = (parametros.get("idVersion") ?? "").trim();
        if (idProyecto !== this.idProyecto || idVersion !== this.idVersion) {
          this.idProyecto = idProyecto;
          this.idVersion = idVersion;
          this.rutaProyecto = idProyecto ? rutaPaginaProyecto(this.router.url, idProyecto) : this.rutaLista;
          this.cargar();
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.destruido = true;
    this.subs.forEach((sub) => sub.unsubscribe());
    this.lectura?.unsubscribe();
  }

  /* ============================ Lo que pinta la plantilla ============================ */

  get version(): VersionResumen | null {
    return this.detalle?.version ?? null;
  }

  get chipVersion(): ChipEstado {
    return chipEstadoVersion(this.version?.estado);
  }

  get creadaPor(): string {
    return this.version ? textoCreadaPor(this.version) : "";
  }

  get cerradaDesde(): string | null {
    return this.version ? textoCerradaDesde(this.version) : null;
  }

  get fechasEtapa(): string {
    return fechasDeEtapa(this.detalle?.etapa);
  }

  get textoEjecuciones(): string {
    return textoCasosEjecutados(this.detalle?.indicadores);
  }

  get textoAprobados(): string {
    return textoDeCasos(this.detalle?.indicadores?.casosEjecutados);
  }

  get abiertasDetectadas(): string {
    const abiertas = this.detalle?.indicadores?.detectadasAbiertas ?? 0;
    return `${abiertas} ${abiertas === 1 ? "abierta" : "abiertas"}`;
  }

  get detectadasVisibles(): FilaIncidencia[] {
    return primeras(this.detectadas, this.todasDetectadas);
  }

  get resueltasVisibles(): FilaIncidencia[] {
    return primeras(this.resueltas, this.todasResueltas);
  }

  /** «Ver las N» / «Ver menos» de una de las dos tablas de incidencias. */
  alternarTodas(cual: "detectadas" | "resueltas"): void {
    if (cual === "detectadas") {
      this.todasDetectadas = !this.todasDetectadas;
    } else {
      this.todasResueltas = !this.todasResueltas;
    }
  }

  mostrando(visibles: number, total: number): string {
    return textoMostrando(visibles, total, "incidencia", "incidencias");
  }

  trackEjecucion(_: number, fila: FilaEjecucion): string {
    return fila.ejecucion.id;
  }

  trackIncidencia(_: number, fila: FilaIncidencia): number {
    return fila.incidencia.idIncidencia;
  }

  /* ============================ Acciones ============================ */

  cargar(): void {
    this.lectura?.unsubscribe();
    if (!this.idProyecto || !this.idVersion) {
      this.detalle = null;
      this.error = "Faltan el proyecto o la versión en la liga.";
      this.pinta();
      return;
    }
    this.cargando = true;
    this.error = "";
    this.pinta();
    this.lectura = this.servicio.detalleVersion(this.idProyecto, this.idVersion).subscribe({
      next: (respuesta) => {
        this.cargando = false;
        if (respuesta?.pCodRetorno === CODIGOS_VERSIONES.EXITO && respuesta.version) {
          this.mostrar(respuesta);
        } else {
          this.detalle = null;
          this.error = respuesta?.pMensaje?.trim() || "No se encontró la versión.";
        }
        this.pinta();
      },
      error: () => {
        this.cargando = false;
        this.detalle = null;
        this.error = "No se pudo consultar la versión. Revisa tu conexión e intenta de nuevo.";
        this.pinta();
      },
    });
  }

  /** «Editar versión»: T1 en modo edición (§5.5); al guardar, vuelve a leer la página. */
  editarVersion(): void {
    const detalle = this.detalle;
    if (!detalle?.puedeEditarVersion || !detalle.version || !detalle.etapa?.id || this.dialog.openDialogs.length > 0) {
      return;
    }
    abrirDialogoNuevaVersion(this.dialog, {
      idProyecto: this.idProyecto,
      idEtapa: detalle.etapa.id,
      nombreProyecto: detalle.proyecto?.nombre,
      nombreEtapa: detalle.etapa.nombre,
      version: detalle.version,
    })
      .afterClosed()
      .subscribe((resultado) => {
        if (!resultado) {
          return;
        }
        if (resultado.editada && resultado.version) {
          this.avisar(`Se actualizó la versión ${resultado.version.nombre}.`);
        }
        this.cargar();
      });
  }

  /** «Registrar pruebas» (T2) en esta versión, o «Editar ejecución» con la ejecución (D5). */
  registrarPruebas(ejecucion: EjecucionPrueba | null = null): void {
    const detalle = this.detalle;
    if (!detalle?.puedeRegistrarPruebas || !detalle.version || !detalle.etapa?.id || this.dialog.openDialogs.length > 0) {
      return;
    }
    abrirRegistrarEjecucion(this.dialog, {
      idProyecto: this.idProyecto,
      idEtapa: detalle.etapa.id,
      nombreProyecto: detalle.proyecto?.nombre,
      nombreEtapa: detalle.etapa.nombre,
      versiones: [detalle.version],
      todasLasVersiones: false,
      idVersion: detalle.version.id,
      ejecutores: detalle.ejecutores ?? [],
      siguienteCiclo: detalle.siguienteCiclo,
      puedeRegistrarPruebas: detalle.puedeRegistrarPruebas,
      ejecucion,
      conocidas: [...(detalle.detectadas ?? []), ...(detalle.resueltas ?? [])],
    })
      .afterClosed()
      .subscribe((resultado) => this.alRegistrar(resultado, !!ejecucion));
  }

  verEvidencia(fila: FilaEjecucion, evidencia: EvidenciaPrueba): void {
    this.subs.push(descargarEvidencia(this.servicio, fila.ejecucion.id, evidencia, (aviso) => this.avisar(aviso)));
  }

  /* ============================ Internas ============================ */

  private alRegistrar(resultado: ResultadoRegistrarEjecucion | undefined, edicion: boolean): void {
    if (!resultado) {
      return;
    }
    const ejecucion = resultado.ejecucion;
    if (ejecucion) {
      const nombre = nombreCiclo(ejecucion.tipo, ejecucion.ciclo);
      this.avisar(edicion ? `Se actualizó la ejecución «${nombre}».` : `Se registró la ejecución «${nombre}».`);
    }
    this.cargar();
  }

  private mostrar(detalle: DetalleVersion): void {
    this.detalle = detalle;
    const etapa = detalle.etapa?.id;
    this.queryEtapa = etapa ? { pestana: "etapas", etapa } : { pestana: "etapas" };
    this.ejecuciones = (detalle.ejecuciones ?? []).map((ejecucion) => filaEjecucion(ejecucion));
    this.detectadas = (detalle.detectadas ?? []).map((incidencia) => filaIncidencia(incidencia));
    this.resueltas = (detalle.resueltas ?? []).map((incidencia) => filaIncidencia(incidencia));
  }

  private avisar(texto: string): void {
    this.snackBar.open(texto, "Cerrar", { duration: 6000, horizontalPosition: "right", verticalPosition: "top" });
  }

  /** La app corre sin zonas: lo que llega de una petición se pinta a mano. */
  private pinta(): void {
    if (!this.destruido) {
      this.cdr.markForCheck();
    }
  }
}

function filaEjecucion(ejecucion: EjecucionPrueba): FilaEjecucion {
  return {
    ejecucion,
    fecha: fechaCorta(ejecucion.fechaEjecucion) ?? SIN_DATO,
    titulo: nombreCiclo(ejecucion.tipo, ejecucion.ciclo),
    casos: typeof ejecucion.casosEjecutados === "number" ? String(ejecucion.casosEjecutados) : SIN_DATO,
    lineaCasos: lineaCasos(ejecucion),
    porcentaje: porcentajeAprobados(ejecucion.casosAprobados, ejecucion.casosEjecutados),
    chip: chipResultado(ejecucion.resultado),
    evidencias: (ejecucion.evidencias ?? []).map((evidencia) => ({ evidencia, nombre: nombreDeEvidencia(evidencia) })),
  };
}

function filaIncidencia(incidencia: IncidenciaVersion): FilaIncidencia {
  return {
    incidencia,
    folio: incidencia.folio === null ? SIN_DATO : `#${incidencia.folio}`,
    prioridad: nombrePrioridad(incidencia.prioridad),
    clasePrioridad: clasePrioridad(incidencia.prioridad),
    claseEstatus: claseEstatus(incidencia.estatus),
    detectadaEn: incidencia.detectadaEn?.nombre?.trim() || "Sin versión identificada",
    fechaResolucion: fechaCorta(incidencia.fechaResolucion) ?? SIN_DATO,
  };
}
